import * as React from 'react';
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { useComposition } from '@/hooks/use-composition';
import { useHasProp } from '@/hooks/use-has-prop';
import { cn } from '@/lib/utils';

const iconVariants = cva('absolute top-3', {
  variants: {
    size: {
      default: 'size-4',
    },
    side: {
      left: 'left-3',
      right: 'right-3',
    },
  },
  defaultVariants: {
    size: 'default',
    side: 'left',
  },
});

interface InputIconProps
  extends React.HTMLAttributes<HTMLOrSVGElement>,
    VariantProps<typeof iconVariants> {}

const InputIcon = forwardRef<HTMLSlotElement, InputIconProps>(
  ({ className, side, ...props }, ref) => (
    <Slot
      data-icon
      ref={ref}
      className={cn(iconVariants({ side }), className)}
      {...props}
    />
  )
);
InputIcon.displayName = 'InputIcon';

const inputVariants = cva(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
  {
    variants: {
      left: {
        true: 'pl-10',
      },
      right: {
        true: 'pr-10',
      },
    },
    defaultVariants: {
      left: false,
      right: false,
    },
  }
);

type InputProps = React.ComponentProps<'input'> &
  VariantProps<typeof inputVariants>;

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => (
    <input
      type={type}
      className={className}
      ref={ref}
      {...props}
    />
  )
);

Input.displayName = 'Input';

interface InputComposition {
  Icon: typeof InputIcon;
}

const Root = forwardRef<
  ElementRef<typeof Input>,
  ComponentPropsWithoutRef<typeof Input>
>(({ children: _children, className, size, ...props }, ref) => {
  const [_, icons] = useComposition(_children, InputIcon.displayName);
  const hasLeftIcon = useHasProp<InputIconProps>(icons ?? [], 'side', 'left');
  const hasRightIcon = useHasProp<InputIconProps>(icons ?? [], 'side', 'right');

  if ((icons ?? []).length > 0) {
    return (
      <div className={cn('relative')}>
        {icons}
        <Input
          ref={ref}
          className={cn(
            inputVariants({
              left: hasLeftIcon,
              right: hasRightIcon,
              className,
            })
          )}
          {...props}
        />
      </div>
    );
  }

  return (
    <Input
      ref={ref}
      className={cn(inputVariants(), className)}
      {...props}
    />
  );
});
Root.displayName = 'Input';

const RootWithComposition: typeof Root & InputComposition = Object.assign(
  Root,
  {
    Icon: InputIcon,
  }
);

export { RootWithComposition as Input };
